// Pointer Events -> engine stroke samples.
//
// - Pen pressure goes through a gamma curve (pressureCurve < 1 = softer feel).
// - Mice report pressure 0.5 (or 0) while pressed; when simulateMousePressure
//   is on, pressure is synthesised from distance travelled (taper-in) and speed
//   (fast = lighter). Simulated pressure is never presented as real pen data:
//   samples carry `simulated: true`.
// - Coalesced events give every hardware sample between frames (pens report at
//   120-240 Hz); predicted events are exposed for cursor display only and are
//   never painted.
// - The stabiliser is an exponential moving average over arc length, so its
//   lag in cells does not depend on the event rate.

export const DEFAULT_INPUT = Object.freeze({
  pressureCurve: 0.8,
  minimumPressure: 0.02,
  simulateMousePressure: true,
  mouseTaperDistance: 18, // cells over which a mouse stroke ramps to full pressure
  stabiliser: 0.0, // 0 = off .. 1 = heavy smoothing
});

export function createStrokeInput(options = {}) {
  const settings = { ...DEFAULT_INPUT, ...options };
  let smooth = null;
  let travelled = 0;
  let last = null;

  const toSample = (event, toCell) => {
    const [x, y] = toCell(event.clientX, event.clientY);
    const isPen = event.pointerType === 'pen' || event.pointerType === 'touch';
    let pressure;
    let simulated = false;
    if (isPen && event.pressure > 0) {
      pressure = Math.pow(Math.max(settings.minimumPressure, Math.min(1, event.pressure)), settings.pressureCurve);
    } else if (settings.simulateMousePressure) {
      simulated = true;
      const ramp = Math.min(1, (travelled + 1) / settings.mouseTaperDistance);
      pressure = 0.25 + 0.5 * Math.sqrt(ramp);
    } else {
      pressure = 0.6;
      simulated = true;
    }
    return {
      x,
      y,
      pressure,
      tiltX: Number.isFinite(event.tiltX) ? event.tiltX : 0,
      tiltY: Number.isFinite(event.tiltY) ? event.tiltY : 0,
      twist: Number.isFinite(event.twist) ? event.twist : 0,
      time: event.timeStamp,
      inputTime: event.timeStamp,
      pointerType: event.pointerType,
      simulated,
    };
  };

  const stabilise = (sample) => {
    if (!smooth || settings.stabiliser <= 0) {
      smooth = { ...sample };
      return sample;
    }
    const d = Math.hypot(sample.x - smooth.x, sample.y - smooth.y);
    const lagCells = 1 + settings.stabiliser * 24;
    const a = 1 - Math.exp(-d / lagCells);
    smooth = { ...sample, x: smooth.x + (sample.x - smooth.x) * a, y: smooth.y + (sample.y - smooth.y) * a };
    return smooth;
  };

  return {
    settings,
    begin(event, toCell) {
      travelled = 0;
      smooth = null;
      const sample = stabilise(toSample(event, toCell));
      last = sample;
      return sample;
    },
    // Returns every sample since the previous event (coalesced where available).
    move(event, toCell) {
      const events = typeof event.getCoalescedEvents === 'function' ? event.getCoalescedEvents() : [];
      const list = events && events.length ? events : [event];
      const out = [];
      for (const e of list) {
        const raw = toSample(e, toCell);
        if (last) travelled += Math.hypot(raw.x - last.x, raw.y - last.y);
        const sample = stabilise(raw);
        last = sample;
        out.push(sample);
      }
      return out;
    },
    predicted(event, toCell) {
      const events = typeof event.getPredictedEvents === 'function' ? event.getPredictedEvents() : [];
      return events.map((e) => toCell(e.clientX, e.clientY));
    },
    end() {
      // Returns the final (stabilised) sample of the stroke.
      const tail = last;
      last = null;
      smooth = null;
      return tail;
    },
  };
}
